import { Box, CircularProgress, Dialog, Grid, TextField, Typography } from '@material-ui/core'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { editUserAttribute, writeInventoryTransactionsAsync } from 'lib/api'
import { useDialog, useErrorHandler } from 'lib/hooks'
import { useFetchCardmarketStock } from 'lib/hooks/useFetchCardmarketStock'
import { awaitGradingScale } from 'lib/hooks/useGradingScale'
import { useInventoryStore } from 'lib/hooks/useInventoryStore'
import { FetchedStockInterface } from 'lib/hooks/useStockStore'
import { useTransactionsSpinnerStore } from 'lib/hooks/useTransactionsSpinnerStore'
import _ from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AggregatedInventoryArticle,
  AppUser,
  computeAggregatedStock,
  countryEditionMap,
  COUNTRY_EDITION,
  FINISH_TYPES_CONVERSIONS,
  GAME_IDS,
  GAME_NAMES,
  GRADING_COMPANY_LABELS,
  IdGame,
  InventoryArticle,
  InventoryFetchState,
  INVENTORY_STEPS,
  INVENTORY_TRANSACTION_CAUSES,
  INVENTORY_TRANSACTION_SOURCES,
  INVENTORY_TRANSACTION_TYPE,
  mapBackendFinishAttributeToInternal,
  PowertoolsPriceTransaction,
  PowertoolsQuantityTransaction,
  PowertoolsTaxTransaction,
  PTInventoryArticle,
  PTInventoryProduct,
  PtProductForPowertoolsTransaction,
} from 'shared'
import { ConflictsSolver } from './ConflictsSolver'
import { ConnectAccount } from './ConnectAccount'
import { FinishImport } from './FinishImport'
import './inventory-transactions.css'
import { InventoryContainer } from './InventoryContainer'
import { PriceSelection } from './PriceSelection'
import { SourceSelection } from './SourceSelection'
import { StockReview } from './StockReview'
import { StockReviewFixGrade } from './StockReviewFixGrade'
import { Welcome } from './Welcome'

export const SyncInventoryPresenter = (): JSX.Element => {
  // const { cardData } = useCardThingData('en')

  const { handle } = useErrorHandler()
  const { t } = useTranslation()
  const { user, setUser } = useUser()
  const fetchCardmarketStock = useFetchCardmarketStock()
  // const fetchArticlesInShoppingCarts = useFetchArticlesInCardmarketShoppingCarts()

  const { step, setStep, setCurrentPtInventory, ptArticles, diffSelectedArticles, selectedSource } =
    useInventoryStore((state) => state)

  const { setShowSpinnerLoading } = useTransactionsSpinnerStore()

  const [setFetchingDialogOpen, FetchingDialog] = useDialog()
  const [setLoadingDialogOpen, LoadingDialog] = useDialog()
  const [inputTokensOpen, setInputTokensOpen] = useState<boolean>(false)
  const [fetchState, setFetchState] = useState(InventoryFetchState.FETCHING)
  fetchState
  const [stockState, setStockState] = useState<FetchedStockInterface>({
    stock: [] as InventoryArticle[],
    sealedStock: [] as InventoryArticle[],
    fetchedAt: '',
  })
  const [workingIncomingStockBeforeUpdatinGrade, setWorkingIncomingStockBeforeUpdatinGrade] =
    useState<InventoryArticle[]>([])
  const [sortedIncomingStock, setSortedIncomingStock] = useState<InventoryArticle[]>([])
  const [incomingAggregatedStock, setIncomingAggregatedStock] = useState<
    AggregatedInventoryArticle[]
  >([])
  const [ptInventory, setPtInventory] = useState<PTInventoryProduct[]>([])
  const [currentlyFetchingGame, setCurrentlyFetchingGame] = useState<string>('')
  const [currentlyFetchingStockType, setCurrentlyFetchingStockType] = useState<string>('')
  setPtInventory
  const [articlesInShoppingCarts, setArticlesInShoppingCarts] = useState<InventoryArticle[]>([])

  // When the user completes the missing grades in StockReviewFixGrade, StockReview would otherwise show a
  // second, visually identical recap requiring another "start import" click. This flag lets StockReview
  // auto-advance to the source selection step once its inventory load + diff are ready, so the user only
  // clicks "proceed" once.
  const [autoProceedAfterGrade, setAutoProceedAfterGrade] = useState<boolean>(false)

  const [key, setKey] = useState<string>('')
  const [secret, setSecret] = useState<string>('')

  useEffect(() => {
    const newWorkingIncomingStock = [...stockState.stock, ...stockState.sealedStock]

    // When the user was force-redirected here for a graded-stock mismatch (forceGradeSync), drop the
    // non-graded cards as soon as the Cardmarket fetch arrives, BEFORE any review box is rendered — so
    // the review screens (StockReviewFixGrade and StockReview) show graded-only counts from the first
    // render, instead of showing the full count and then visibly dropping once the grades are defined.
    // Cards that are graded but still missing a grade keep their gradingCompany, so they're retained
    // here and still surface in the grade-completion dialog; a card the user later marks as "not graded"
    // (clearing gradingCompany) is removed by the re-filter in fillSortedIncomingStock.
    const incomingForReview = user?.featureFlags?.forceGradeSync
      ? newWorkingIncomingStock.filter((article) => !!article.gradingCompany)
      : newWorkingIncomingStock

    const cardsWithoutGrade = incomingForReview.filter(
      (article) => article.gradingCompany && article.grade === undefined
    ).length

    if (cardsWithoutGrade > 0) {
      // Some graded cards still need a grade: route to StockReviewFixGrade to complete them.
      setWorkingIncomingStockBeforeUpdatinGrade(incomingForReview)
    } else {
      // No grades to fix: finalize the incoming stock straight away through the same funnel.
      fillSortedIncomingStock(incomingForReview)
    }
  }, [stockState])

  const fillSortedIncomingStock = (newIncomingStock: InventoryArticle[]) => {
    // When the user was force-redirected here for a graded-stock mismatch (`forceGradeSync`), narrow the
    // Cardmarket side to graded cards only. This runs AFTER the grade-completion step, so `gradingCompany`
    // already encodes everything: structured-comment cards (always graded), non-structured cards the user
    // confirmed as graded, and — excluded — the ones the user unchecked as "not graded"
    // (CompleteDataInGradedCards clears their gradingCompany). Non-graded Cardmarket stock stays out of
    // the whole comparison, so a later "keep all from PowerTools" can never wipe it.
    const stockToUse = user?.featureFlags?.forceGradeSync
      ? newIncomingStock.filter((article) => !!article.gradingCompany)
      : newIncomingStock
    const sortedStock = [...stockToUse].sort((a, b) => a.price - b.price)
    setSortedIncomingStock(sortedStock)
    setWorkingIncomingStockBeforeUpdatinGrade([])
  }

  useEffect(() => {
    if (sortedIncomingStock.length > 0) {
      const incomingAggregatedStock = computeAggregatedStock(
        sortedIncomingStock,
        articlesInShoppingCarts
      )

      setIncomingAggregatedStock(incomingAggregatedStock)
    }
  }, [sortedIncomingStock])

  const fetchUserStockAllGames = async (tokens: { key: string; secret: string } | null) => {
    const stock: InventoryArticle[] = []
    const sealedStock: InventoryArticle[] = []

    for (const idGame of GAME_IDS) {
      // *** only for testing, comment for production ***
      // if ([IdGame.Cyberpunk].includes(idGame)) {
      setCurrentlyFetchingGame(GAME_NAMES[idGame])
      setCurrentlyFetchingStockType('stock')
      const gameStockData = await fetchCardmarketStock(idGame as IdGame, tokens)
      stock.push(...gameStockData.stock)
      sealedStock.push(...gameStockData.sealedStock)
      // }
    }

    setCurrentlyFetchingGame('')
    setCurrentlyFetchingStockType('generic accessories')
    const genericStockData = await fetchCardmarketStock(99 as IdGame, tokens)
    sealedStock.push(...genericStockData.sealedStock)

    setCurrentlyFetchingStockType('')
    return { stock, sealedStock }
  }

  const fetchStock = useCallback(
    async (tokens): Promise<FetchedStockInterface> => {
      let newState = {
        stock: [] as InventoryArticle[],
        sealedStock: [] as InventoryArticle[],
        fetchedAt: '',
      }
      setFetchState(InventoryFetchState.FETCHING)
      setFetchingDialogOpen(true)
      try {
        const { stock, sealedStock } = await fetchUserStockAllGames(tokens)

        newState = {
          stock,
          sealedStock,
          fetchedAt: new Date().toISOString(),
        }

        setFetchState(InventoryFetchState.IDLE)
      } catch (error: any) {
        handle(error)
        if (error?.response?.data?.error?.message === 'missingCardmarketConnection') {
          setFetchState(InventoryFetchState.FAILED_DISCONNECTED_ACCOUNT)
          throw new Error('missingCardmarketConnection')
        } else {
          setFetchState(InventoryFetchState.FAILED_UNKNOWN)
        }
      }
      setFetchingDialogOpen(false)

      return newState
    },
    [fetchCardmarketStock]
  )

  const writeInventoryTransactions = async (
    ptQuantityTransactions: PowertoolsQuantityTransaction[],
    ptTaxTransactions: PowertoolsTaxTransaction[],
    ptPriceTransactions: PowertoolsPriceTransaction[],
    ptTransactions: any[],
    platform: string = INVENTORY_TRANSACTION_TYPE.POWERTOOLS
  ) => {
    await writeInventoryTransactionsAsync(
      ptQuantityTransactions,
      ptTaxTransactions,
      ptPriceTransactions,
      ptTransactions,
      platform
    )
  }

  const checkUserConflictsAndUpdate = async (user: AppUser) => {
    let newUser = _.cloneDeep(user)
    if (!user.ptInventory?.nightly) {
      return newUser
    }
    const { numberOfConflicts, foundAt, resolved } = user.ptInventory?.nightly
    const now = new Date()
    // TODO check why foundAt is of type string and not date
    if (numberOfConflicts > 0 && !resolved && new Date(foundAt).getTime() < now.getTime()) {
      const resp = await editUserAttribute(
        {
          nightly: {
            ...user.ptInventory.nightly,
            resolved: true,
            resolvedAt: now,
          },
        },
        'ptInventory'
      )
      newUser = { ...user!, ...resp }
    }
    return newUser
  }

  const updateLastManualSyncWithCardmarketAt = async (user: AppUser) => {
    let newUser = _.cloneDeep(user)
    const newPtInventory = {
      ...user.ptInventory,
      lastManualSyncWithCardmarketAt: new Date(),
    }
    const resp = await editUserAttribute(newPtInventory, 'ptInventory')
    newUser = { ...newUser!, ...resp }
    return newUser
  }

  const getMatchingPtInventoryArticles = (
    articles: PTInventoryArticle[],
    a: AggregatedInventoryArticle
  ) => {
    return articles.filter((article) => isTheSameArticle(article, a))
  }

  const getMatchingAggregatedInventoryArticle = (
    articles: AggregatedInventoryArticle[],
    a: PTInventoryArticle
  ) => {
    return articles.find((article) => isTheSameArticle(a, article))
  }

  const isTheSameArticle = (a: PTInventoryArticle, b: AggregatedInventoryArticle) => {
    const ptProduct = a.ptProduct

    // siccome PTInventoryArticle è un dato che arriva dal BE, bisogna fixare il finishType affinchè matchi con
    // AggregatedInventory che è un dato del FE

    const countryEdition = countryEditionMap.find(
      (entry) => entry.code === (ptProduct.countryEdition as COUNTRY_EDITION)
    )!.value

    return (
      Number(ptProduct.mkmProductId) === b.card._id &&
      ptProduct.condition === b.condition &&
      ptProduct.idLanguage === b.idLanguage &&
      ptProduct.isFirstEd === b.isFirstEd &&
      mapBackendFinishAttributeToInternal(ptProduct.finishType) === b.finishType &&
      ptProduct.isSigned === b.isSigned &&
      countryEdition === b.countryEdition &&
      GRADING_COMPANY_LABELS.find((entry) => entry.code === a.ptProduct.gradingCompany)?.code ===
        b.gradingCompany &&
      a.ptProduct.grade === b.grade
    )
  }

  const isTheSameAggregatedArticle = (
    a: AggregatedInventoryArticle,
    b: AggregatedInventoryArticle
  ) => {
    return (
      a.card._id === b.card._id &&
      a.condition === b.condition &&
      a.idLanguage === b.idLanguage &&
      a.isFirstEd === b.isFirstEd &&
      a.finishType === b.finishType &&
      a.isSigned === b.isSigned &&
      a.countryEdition === b.countryEdition &&
      a.gradingCompany === b.gradingCompany &&
      a.grade === b.grade
    )
  }

  const getLocationsForDeduction = (
    article: PTInventoryArticle,
    totalQuantityToSubtract: number
  ) => {
    const locationsForDeduction = []
    // 1. sort locations by quantity (decreasing). If two locations have the same quantity, sort them by name (ascending)
    const sortedLocations = article.locations?.sort((a, b) => {
      if (a.quantity === b.quantity) {
        const locationA = a.name ?? ''
        const locationB = b.name ?? ''
        return locationA.localeCompare(locationB)
      }
      return b.quantity - a.quantity
    })

    // 2. add locations to the output list until the sum of the quantities is greater than or equal to the quantity to subtract
    let quantityToSubtract = totalQuantityToSubtract
    for (const location of sortedLocations) {
      const deltaQuantity = Math.min(quantityToSubtract, location.quantity)
      locationsForDeduction.push({ deltaQuantity: -deltaQuantity, ...location })
      quantityToSubtract -= deltaQuantity
      if (quantityToSubtract === 0) {
        break
      }
    }

    // 3. return the list of locations
    return locationsForDeduction
  }

  const handleConfirmSelection = async (selectedAggregatedStock: AggregatedInventoryArticle[]) => {
    setLoadingDialogOpen(true)
    const quantityTransactions: PowertoolsQuantityTransaction[] = []
    const taxTransactions: PowertoolsTaxTransaction[] = []
    const priceTransactions: PowertoolsPriceTransaction[] = []
    const powertoolsTransactions: any[] = []

    const mkmQuantityTransactions: PowertoolsQuantityTransaction[] = []
    const mkmTransactions: any[] = []

    selectedAggregatedStock.forEach((selectedAggregatedArticle) => {
      // if there are N locations for the same item, we need to create N quantity transactions

      const matchingPtInventoryArticles = getMatchingPtInventoryArticles(
        ptArticles,
        selectedAggregatedArticle
      )

      let existingArticle: PTInventoryArticle | undefined
      if (matchingPtInventoryArticles.length > 0) {
        // create an article that has the same properties as an existing article (we can take the first of the matching ones, it doesn't matter)
        // and then add the locations and quantities of all the other matching articles: we need to do this in order to make the correct quantity
        // diffs and transactions
        existingArticle = _.cloneDeep(matchingPtInventoryArticles[0])
        const remainingMatchingPtInventoryArticles = matchingPtInventoryArticles.slice(1)
        remainingMatchingPtInventoryArticles.forEach((article) => {
          article.locations?.forEach((location) => {
            existingArticle!.locations?.push(location)
            existingArticle!.quantity += location.quantity
          })
        })
      }

      let currentPrice = 0
      let ptProductId = -1
      if (existingArticle) {
        currentPrice = existingArticle.baseSellPrice
        ptProductId = existingArticle.ptProduct._id
      }

      const ptProduct: PtProductForPowertoolsTransaction = {
        _id: ptProductId,
        sku: selectedAggregatedArticle.sku,
        mkmProductId: selectedAggregatedArticle.idProduct,
        condition: selectedAggregatedArticle.condition,
        idLanguage: selectedAggregatedArticle.idLanguage,
        isFirstEd: selectedAggregatedArticle.isFirstEd,
        finishType: FINISH_TYPES_CONVERSIONS.find(
          (entry) => entry.label === selectedAggregatedArticle.finishType
        )!.code, // siccome il dato va al BE deve essere convertito in numero
        isSigned: selectedAggregatedArticle.isSigned,
        countryEdition: countryEditionMap.find(
          (edition) => edition.value === selectedAggregatedArticle.countryEdition
        )!.code,
        gradingCompany: GRADING_COMPANY_LABELS.find(
          (entry) => entry.code === selectedAggregatedArticle.gradingCompany
        )?.code,
        grade: selectedAggregatedArticle.grade,
      }
      let quantityChanged = false

      // Quantity transactions must be written only if:
      // 1. existing article is missing OR
      // 2. total quantity of the incoming article is different from the total quantity of the existing one. Total quantity on PowerTools must take reservations into account
      if (
        !existingArticle ||
        (existingArticle &&
          existingArticle.quantity - existingArticle.reservedQuantity !==
            selectedAggregatedArticle.quantity)
      ) {
        const selectedAggregatedQuantity = selectedAggregatedArticle.quantity

        if (existingArticle) {
          const existingQuantity = existingArticle.quantity
          const deltaQuantity = selectedAggregatedQuantity - existingQuantity

          if (deltaQuantity < 0) {
            const locationsForDeduction = getLocationsForDeduction(
              existingArticle,
              Math.abs(deltaQuantity)
            )
            locationsForDeduction.forEach((locationForDeduction) => {
              const quantityTransaction: PowertoolsQuantityTransaction = {
                type: INVENTORY_TRANSACTION_TYPE.QUANTITY,
                userId: user._id,
                deltaQuantity: locationForDeduction.deltaQuantity,
                location: locationForDeduction.name,
              }
              quantityTransactions.push(quantityTransaction)
              quantityChanged = true

              const taxTransaction: PowertoolsTaxTransaction = {
                type: INVENTORY_TRANSACTION_TYPE.TAX_TRANSACTION,
                userId: user._id,
                deltaQuantity: locationForDeduction.deltaQuantity,
                location: locationForDeduction.name,
                transactionType: 0, // not order
                taxationScheme: undefined,
              }
              taxTransactions.push(taxTransaction)

              // create powertools transaction

              const powertoolsTransaction = {
                powertoolsQuantityTransactionIndex: quantityTransactions.length - 1,
                powertoolsTaxTransactionIndex: taxTransactions.length - 1,
                userId: user._id,
                aggregatedArticle: selectedAggregatedArticle,
                cause: INVENTORY_TRANSACTION_CAUSES.MANUAL_SYNC,
                source: selectedAggregatedArticle.source || '',
                ptProduct: ptProduct,
              }
              powertoolsTransactions.push(powertoolsTransaction)
            })
          } else if (deltaQuantity > 0) {
            const quantityTransaction: PowertoolsQuantityTransaction = {
              type: INVENTORY_TRANSACTION_TYPE.QUANTITY,
              userId: user._id,
              deltaQuantity,
              location: existingArticle.locations ? existingArticle.locations[0].name : '',
            }
            quantityTransactions.push(quantityTransaction)
            quantityChanged = true

            const taxTransaction: PowertoolsTaxTransaction = {
              type: INVENTORY_TRANSACTION_TYPE.TAX_TRANSACTION,
              userId: user._id,
              deltaQuantity: deltaQuantity,
              location: existingArticle.locations ? existingArticle.locations[0].name : '',
              transactionType: 0, // not order
              taxationScheme: undefined,
            }
            taxTransactions.push(taxTransaction)

            // create powertools transaction
            const powertoolsTransaction = {
              powertoolsQuantityTransactionIndex: quantityTransactions.length - 1,
              powertoolsTaxTransactionIndex: taxTransactions.length - 1,
              userId: user._id,
              aggregatedArticle: selectedAggregatedArticle,
              cause: INVENTORY_TRANSACTION_CAUSES.MANUAL_SYNC,
              source: selectedAggregatedArticle.source || '',
              ptProduct: ptProduct,
            }
            powertoolsTransactions.push(powertoolsTransaction)
          }
        } else {
          // existing article is missing
          const deltaQuantity = selectedAggregatedQuantity

          if (deltaQuantity !== 0) {
            const quantityTransaction: PowertoolsQuantityTransaction = {
              type: INVENTORY_TRANSACTION_TYPE.QUANTITY,
              userId: user._id,
              deltaQuantity,
              location: selectedAggregatedArticle.locations
                ? selectedAggregatedArticle.locations[0].location
                : '',
            }
            quantityTransactions.push(quantityTransaction)
            quantityChanged = true

            const taxTransaction: PowertoolsTaxTransaction = {
              type: INVENTORY_TRANSACTION_TYPE.TAX_TRANSACTION,
              userId: user._id,
              deltaQuantity: deltaQuantity,
              location: selectedAggregatedArticle.locations
                ? selectedAggregatedArticle.locations[0].location
                : '',
              transactionType: 0, // not order
              taxationScheme: undefined,
            }
            taxTransactions.push(taxTransaction)

            // create powertools transaction
            const powertoolsTransaction = {
              powertoolsQuantityTransactionIndex: quantityTransactions.length - 1,
              powertoolsTaxTransactionIndex: taxTransactions.length - 1,
              userId: user._id,
              aggregatedArticle: selectedAggregatedArticle,
              cause: INVENTORY_TRANSACTION_CAUSES.MANUAL_SYNC,
              source: selectedAggregatedArticle.source || '',
              ptProduct: ptProduct,
            }
            powertoolsTransactions.push(powertoolsTransaction)
          }
        }
      }

      if (selectedAggregatedArticle.selectedPrice !== currentPrice) {
        const priceTransaction: PowertoolsPriceTransaction = {
          type: INVENTORY_TRANSACTION_TYPE.PRICE,
          userId: user._id,
          price: selectedAggregatedArticle.selectedPrice,
          currency: user.currency,
        }
        priceTransactions.push(priceTransaction)

        // create powertools transaction
        if (quantityChanged) {
          // TODO: controllare questo caso per capire come dovrebbe essere impostata la tax transactions

          powertoolsTransactions[
            powertoolsTransactions.length - 1
          ].powertoolsPriceTransactionIndex = priceTransactions.length - 1
        } else {
          const powertoolsTransaction = {
            powertoolsPriceTransactionIndex: priceTransactions.length - 1,
            userId: user._id,
            aggregatedArticle: selectedAggregatedArticle,
            cause: INVENTORY_TRANSACTION_CAUSES.MANUAL_SYNC,
            source: selectedAggregatedArticle.source || '',
            ptProduct: ptProduct,
          }
          powertoolsTransactions.push(powertoolsTransaction)
        }
      }

      if (selectedAggregatedArticle.source == INVENTORY_TRANSACTION_SOURCES.POWERTOOLS) {
        let mkmArticleQuantity = 0
        const mkmArticle = incomingAggregatedStock.find((element) => {
          return isTheSameAggregatedArticle(element, selectedAggregatedArticle)
        })
        if (mkmArticle && mkmArticle.locations) {
          mkmArticleQuantity = mkmArticle.locations
            .map((element) => {
              return element.quantity
            })
            .reduce((result: number, current: number) => {
              return result + current
            }, 0)
        }
        let ptArticleQuantity = 0
        if (selectedAggregatedArticle.locations) {
          ptArticleQuantity = selectedAggregatedArticle.locations
            .map((element) => {
              return element.quantity
            })
            .reduce((result: number, current: number) => {
              return result + current
            }, 0)
        }

        if (existingArticle) {
          ptArticleQuantity -= existingArticle.reservedQuantity
        }

        const deltaQuantity = ptArticleQuantity - mkmArticleQuantity
        if (deltaQuantity != 0) {
          const quantityTransaction: PowertoolsQuantityTransaction = {
            type: INVENTORY_TRANSACTION_TYPE.QUANTITY,
            userId: user._id,
            location: selectedAggregatedArticle.locations
              ? selectedAggregatedArticle.locations[0].location
              : '',
            deltaQuantity,
          }
          mkmQuantityTransactions.push(quantityTransaction)
          quantityChanged = true

          const mkmTransaction = {
            powertoolsQuantityTransactionIndex: mkmQuantityTransactions.length - 1,
            userId: user._id,
            aggregatedArticle: selectedAggregatedArticle,
            cause: INVENTORY_TRANSACTION_CAUSES.MANUAL_SYNC,
            source: selectedAggregatedArticle.source || '',
            ptProduct: ptProduct,
          }
          mkmTransactions.push(mkmTransaction)
        }
      }
    })

    // search for articles which have been unselected
    ptArticles.forEach((article) => {
      const selectedAggregatedArticle = getMatchingAggregatedInventoryArticle(
        selectedAggregatedStock,
        article
      )
      if (!selectedAggregatedArticle) {
        // do not include articles that are going to 0 total quantity because of reservations
        // otherwise they will be doubly subtracted when the order is shipped
        if (article.quantity === article.reservedQuantity) {
          return
        }

        article.locations?.forEach((location) => {
          const ptQuantityTransaction: PowertoolsQuantityTransaction = {
            type: INVENTORY_TRANSACTION_TYPE.QUANTITY,
            userId: user._id,
            deltaQuantity: -location.quantity,
            location: location.name,
          }
          quantityTransactions.push(ptQuantityTransaction)

          const taxTransaction: PowertoolsTaxTransaction = {
            type: INVENTORY_TRANSACTION_TYPE.TAX_TRANSACTION,
            userId: user._id,
            deltaQuantity: -location.quantity,
            location: location.name,
            transactionType: 0, // not order
            taxationScheme: undefined,
          }
          taxTransactions.push(taxTransaction)

          // create powertools transaction
          const powertoolsTransaction = {
            powertoolsQuantityTransactionIndex: quantityTransactions.length - 1,
            powertoolsTaxTransactionIndex: taxTransactions.length - 1,
            userId: user._id,
            article,
            cause: INVENTORY_TRANSACTION_CAUSES.MANUAL_SYNC,
            source: '',
            ptProduct: article.ptProduct,
          }
          powertoolsTransactions.push(powertoolsTransaction)
        })
      }
    })

    await writeInventoryTransactions(
      quantityTransactions,
      taxTransactions,
      priceTransactions,
      powertoolsTransactions
    )

    await writeInventoryTransactions(
      mkmQuantityTransactions,
      [],
      [],
      mkmTransactions,
      INVENTORY_TRANSACTION_TYPE.CARDMARKET
    )
    // if the user had conflicts reported by the nightly job, mark them as resolved
    let updatedUser = await checkUserConflictsAndUpdate(user)

    // if the selected source for this import was cardmarket, update the lastManualSyncWithCardmarketAt field
    if (selectedSource === INVENTORY_TRANSACTION_SOURCES.CARDMARKET) {
      updatedUser = await updateLastManualSyncWithCardmarketAt(updatedUser!)
    }

    // The user was forced here by the graded-stock cleanup (`forceGradeSync`); completing the sync is
    // what they were asked to do, so clear the flag — this is what stops the global redirect in
    // AppLayout. We send the WHOLE featureFlags object (spread + override) rather than a partial: that
    // reassigns the Mixed `featureFlags` path through Mongoose's document setter, which persists it on
    // save WITHOUT needing markModified (a partial body would instead wipe the other flags).
    if (updatedUser?.featureFlags?.forceGradeSync) {
      const clearedFeatureFlags = { ...updatedUser.featureFlags, forceGradeSync: false }
      await editUserAttribute(clearedFeatureFlags, 'featureFlags')
      // Also record WHEN the user completed the forced graded sync, next to the other PT-inventory sync
      // tracking. Stored only for users who actually complete it (absent for everyone else). ptInventory
      // is a structured (non-Mixed) field, so set + save persists it normally.
      const ptInventoryWithGradeSync = {
        ...updatedUser.ptInventory,
        gradeSyncCompletedAt: new Date(),
      }
      await editUserAttribute(ptInventoryWithGradeSync, 'ptInventory')
      updatedUser = {
        ...updatedUser,
        featureFlags: clearedFeatureFlags,
        ptInventory: ptInventoryWithGradeSync,
      }
    }

    setUser(updatedUser!)

    // setAggregatedStock([])
    // setConflictSolvedData([])
    setStep(INVENTORY_STEPS.FINISH.idx)
    setShowSpinnerLoading(true)
    setLoadingDialogOpen(false)
  }

  const handleImportStock = async () => {
    const start = Date.now()
    const tokens = null

    // Which grades a company awards decides what we can read off a comment, and the whole fetch runs in one pass:
    // started before the scale is here it would parse the first games against nothing, accepting grades no company
    // awards and sending good ones to the manual step.
    await awaitGradingScale()
    // if (['development', 'staging'].includes(process.env.NODE_ENV)) {
    //   tokens = {
    //     key: 'INVENTORY_IMPORT_TEST_TOKEN_KEY',
    //   }
    // }
    const newStockState = await fetchStock(tokens)

    // take into account articles in other users' shopping carts
    // if (!process.env.REACT_APP_NODE_ENV) {
    //   const newArticlesInShoppingCarts = await fetchArticlesInShoppingCarts()
    //   setArticlesInShoppingCarts(newArticlesInShoppingCarts)
    // } else

    setArticlesInShoppingCarts([])

    setStockState(newStockState)
    const end = Date.now()
    console.log(`import stock took ${(end - start) / 1000} seconds`)
  }

  const handleImportOtherUserStock = async () => {
    const tokens = {
      key: key,
      secret: secret,
    }
    const start = Date.now()
    const newStockState = await fetchStock(tokens)
    setStockState(newStockState)
    const end = Date.now()
    console.log(`import stock took ${(end - start) / 1000} seconds`)
  }

  useEffect(() => {
    setCurrentPtInventory(ptInventory)
  }, [ptInventory])

  const closeInputTokensDialog = () => {
    setInputTokensOpen(false)
    setKey('')
    setSecret('')
  }

  const handleConfirmDiffViewer = async (selectedData: AggregatedInventoryArticle[]) => {
    console.log(`handleConfirmDiffViewer`)
    // setConflictSolvedData(selectedData)
    // check if there are prices to be selected
    const pricesToBeSelected = selectedData.filter(
      (article: AggregatedInventoryArticle) => article.minPrice !== article.maxPrice
    )
    if (pricesToBeSelected.length > 0) {
      setStep(INVENTORY_STEPS.PRICE_SELECTION.idx)
    } else {
      console.log(`no need to open price selection`)
      await handleConfirmSelection(selectedData)
    }
  }

  const handleConfirmPriceSelection = async (selectedData: AggregatedInventoryArticle[]) => {
    console.log(`handleConfirmPriceSelection`)
    await handleConfirmSelection(selectedData)
  }

  return (
    <>
      {step === INVENTORY_STEPS.WELCOME.idx && (
        <Box className="inventoryContainer">
          <Welcome />
        </Box>
      )}
      {step === INVENTORY_STEPS.CONNECT_ACCOUNT.idx && (
        <InventoryContainer>
          <ConnectAccount handleImportStock={handleImportStock} />
        </InventoryContainer>
      )}
      {step === INVENTORY_STEPS.STOCK_REVIEW.idx && (
        <InventoryContainer>
          {workingIncomingStockBeforeUpdatinGrade.length > 0 ? (
            <StockReviewFixGrade
              workingIncomingStockBeforeUpdatinGrade={workingIncomingStockBeforeUpdatinGrade}
              setWorkingIncomingStockBeforeUpdatinGrade={setWorkingIncomingStockBeforeUpdatinGrade}
              fillSortedIncomingStock={fillSortedIncomingStock}
              onGradesCompleted={() => setAutoProceedAfterGrade(true)}
            />
          ) : (
            <StockReview
              incomingStock={sortedIncomingStock}
              incomingAggregatedStock={incomingAggregatedStock}
              autoProceed={autoProceedAfterGrade}
              onAutoProceeded={() => setAutoProceedAfterGrade(false)}
            />
          )}
        </InventoryContainer>
      )}
      {step === INVENTORY_STEPS.SOURCE_SELECTION.idx && (
        <InventoryContainer>
          <SourceSelection
            incomingStock={sortedIncomingStock}
            incomingAggregatedStock={incomingAggregatedStock}
          />
        </InventoryContainer>
      )}
      {step === INVENTORY_STEPS.SOLVE_CONFLICTS.idx && (
        <InventoryContainer>
          <ConflictsSolver
            incomingStock={sortedIncomingStock}
            onConfirm={handleConfirmDiffViewer}
          />
        </InventoryContainer>
      )}
      {step === INVENTORY_STEPS.PRICE_SELECTION.idx && (
        <InventoryContainer smallSpacer>
          <PriceSelection
            aggregatedStock={diffSelectedArticles}
            onConfirm={handleConfirmPriceSelection}
          />
        </InventoryContainer>
      )}
      {step === INVENTORY_STEPS.FINISH.idx && (
        <InventoryContainer>
          <FinishImport />
        </InventoryContainer>
      )}

      <FetchingDialog
        title={t('common.loading')}
        content={
          <Box height="4rem" width="25 rem">
            <Grid container>
              <Grid item xs={10} style={{ alignSelf: 'center' }}>
                <Typography variant="body1" align="center">
                  {t('ptInventory.stockReview.fetching', {
                    gameName: currentlyFetchingGame,
                    stockType: currentlyFetchingStockType,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <CircularProgress size={50} />
              </Grid>
            </Grid>
          </Box>
        }
      />

      <LoadingDialog
        title="Loading..."
        content={
          <Box marginTop={3} height="8rem" width="fit-content" margin="0 auto">
            <CircularProgress size={100} />
          </Box>
        }
      />

      <Dialog
        title="Access tokens"
        maxWidth="xl"
        open={inputTokensOpen}
        onClose={closeInputTokensDialog}
      >
        <Box width={{ xs: 'auto', sm: '300px' }} margin={{ xs: 2, sm: 4 }}>
          <Box>
            <TextField
              label="Key"
              value={key}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKey(e.target.value)}
            />
          </Box>
          <Box>
            <TextField
              label="Secret"
              value={secret}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSecret(e.target.value)}
            />
          </Box>
          <Box marginTop={2}>
            <Button
              variant="contained"
              onClick={() => {
                handleImportOtherUserStock()
                closeInputTokensDialog()
              }}
            >
              Start
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  )
}
